import { useState } from 'react';

import Select from 'react-select';
import DatePicker from 'react-date-picker';

import ButtonLoader from '../ButtonLoader';

import { getSydneyDateObject, formatDateToSydneyTimeZone, buildSydneyDateTime } from '../../utils/dateUtils';

const typeSelectOptions = [{
  value: 'work',
  label: 'Work'
}, {
  value: 'sick',
  label: 'Sick Leave'
}]

const hourOptions = [];
const minuteOptions = [];

for (let i = 0; i <= 23; i++) {
  hourOptions.push({
    value: i,
    label: i
  });
}

for (let i = 0; i <= 59; i++) {
  minuteOptions.push({
    value: i,
    label: i
  });
}

const TimeEntryForm = ({ title, shouldShowLabourerField = true, shouldShowProjectField = true, projectSelectOptions, labourerSelectOptions, timeEntry, isSubmitting, onSubmit, onCloseClicked }) => {
  const [project, setProject] = useState(timeEntry ? { value: timeEntry.project._id, label: timeEntry.project?.name } : null);
  const [labourer, setLabourer] = useState(timeEntry ? { value: timeEntry.labourer._id, label: timeEntry.labourer.user.fullName } : null);

  const sydneyStart = timeEntry ? getSydneyDateObject(timeEntry.startTime) : null;
  const sydneyEnd = (timeEntry && timeEntry?.endTime) ? getSydneyDateObject(timeEntry.endTime) : null;

  const [startDate, setStartDate] = useState(
    timeEntry ? new Date(formatDateToSydneyTimeZone(timeEntry.startTime, 'yyyy-MM-dd')) : new Date()
  );
  const [startHour, setStartHour] = useState(
    sydneyStart ? { label: sydneyStart.getHours(), value: sydneyStart.getHours() } : { label: 0, value: 0 }
  );
  const [startMinute, setStartMinute] = useState(
    sydneyStart ? { label: sydneyStart.getMinutes(), value: sydneyStart.getMinutes() } : { label: 0, value: 0 }
  );

  const [endDate, setEndDate] = useState(
    sydneyEnd ? new Date(formatDateToSydneyTimeZone(timeEntry.endTime, 'yyyy-MM-dd')) : new Date()
  );
  const [endHour, setEndHour] = useState(
    sydneyEnd ? { label: sydneyEnd.getHours(), value: sydneyEnd.getHours() } : { label: 0, value: 0 }
  );
  const [endMinute, setEndMinute] = useState(
    sydneyEnd ? { label: sydneyEnd.getMinutes(), value: sydneyEnd.getMinutes() } : { label: 0, value: 0 }
  );

  const [type, setType] = useState(timeEntry ? { label: timeEntry.type, value: timeEntry.type } : { label: 'Work', value: 'work' });

  const handleSubmit = (e) => {
    e.preventDefault();

    const startTime = buildSydneyDateTime(startDate, startHour.value, startMinute.value);
    const endTime = buildSydneyDateTime(endDate, endHour.value, endMinute.value);

    const payload = {
      type: type.value,
      startTime,
      endTime
    };

    if (shouldShowLabourerField) payload.labourerId = labourer.value;
    if (shouldShowProjectField) payload.projectId = project.value;

    onSubmit(payload);
  };

  return (
    <div className="bg-white rounded-lg border border-ase-blue w-full md:w-[600px] overflow-visible">
      <div className="flex items-center justify-between text-lg bg-ase-blue text-white p-4 font-bold rounded-t-md">
        <p>{title}</p>

        <p
          onClick={onCloseClicked}
          className="font-bold cursor-pointer text-2xl"
        >
          X
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col w-full overflow-visible p-4 gap-4"
      >
        <div className='flex flex-col gap-4'>
          {shouldShowProjectField && (
            <label className='flex flex-col gap-1'>
              <span>Project</span>

              <Select
                value={project}
                onChange={setProject}
                options={projectSelectOptions}
                required={true}
                noOptionsMessage={() => "This labourer is currently not allocated to any projects."}
              />
            </label>
          )}

          {shouldShowLabourerField && (
            <label className='flex flex-col gap-1'>
              <span>Labourer</span>

              <Select
                value={labourer}
                onChange={setLabourer}
                options={labourerSelectOptions}
                required={true}
              />
            </label>
          )}

          <label className='flex flex-col gap-1'>
            <span>Type</span>

            <Select
              value={type}
              onChange={setType}
              options={typeSelectOptions}
              required={true}
            />
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex flex-col gap-1 w-full">
              Start date

              <DatePicker
                value={startDate}
                onChange={setStartDate}
                required={true}
                clearIcon={null}
                format="d/M/yyyy"
                className="h-[46px]"
                maxDetail="month"
                minDetail="year"
              />
            </label>

            <div className='grid grid-cols-2 gap-4'>
              <label className="flex flex-col gap-1 w-full">
                Start hour

                <Select
                  value={startHour}
                  onChange={setStartHour}
                  options={hourOptions}
                  required={true}
                />
              </label>

              <label className="flex flex-col gap-1 w-full">
                Start minute

                <Select
                  value={startMinute}
                  onChange={setStartMinute}
                  options={minuteOptions}
                  required={true}
                />
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex flex-col gap-1 w-full">
              End date

              <DatePicker
                value={endDate}
                onChange={setEndDate}
                required={true}
                clearIcon={null}
                format="d/M/yyyy"
                className="h-[46px]"
                maxDetail="month"
                minDetail="year"
              />
            </label>

            <div className='grid grid-cols-2 gap-4'>
              <label className="flex flex-col gap-1 w-full">
                End hour

                <Select
                  value={endHour}
                  onChange={setEndHour}
                  options={hourOptions}
                  required={true}
                />
              </label>

              <label className="flex flex-col gap-1 w-full">
                End minute

                <Select
                  value={endMinute}
                  onChange={setEndMinute}
                  options={minuteOptions}
                  required={true}
                />
              </label>
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="ase-button-primary h-[48px] w-full flex items-center justify-center disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting ? <ButtonLoader /> : 'Submit'}
        </button>
      </form>
    </div>
  );
};

export default TimeEntryForm;
